import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatINR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, IndianRupee, MessageSquare, Phone, Calendar, Download, Upload, Check, X, AlertCircle, Eye, Settings, UserCheck, Pencil, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import EditStudentDialog from "@/components/EditStudentDialog";
import StudentCredentialsDialog from "@/components/StudentCredentialsDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

function FeeBadgeLg({ status }) {
  const labels = { paid: "All Paid", partial: "Partially Paid", overdue: "Overdue", due: "Due", unset: "Fees Not Set" };
  return <Badge variant="outline" className={`status-${status === "unset" ? "inquiry" : status} text-sm px-3 py-1`}>{labels[status]}</Badge>;
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [openPay, setOpenPay] = useState(false);
  const [openFee, setOpenFee] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCreds, setOpenCreds] = useState(false);
  const [pay, setPay] = useState({ amount: "", mode: "upi", notes: "", installment_no: "", paid_on: new Date().toISOString().slice(0, 10) });
  const [feeForm, setFeeForm] = useState({ installments: "3", discount: "0", custom_total: "" });

  const load = async () => {
    const { data } = await api.get(`/students/${id}`);
    setStudent(data);
  };
  useEffect(() => { load(); }, [id]);

  // Pre-populate fee form when student data loads (for reconfigure)
  useEffect(() => {
    if (student && student.installments_schedule?.length > 0) {
      setFeeForm({
        installments: String(student.installments || student.installments_schedule.length),
        discount: String(student.discount || 0),
        custom_total: student.custom_total_fee ? String(student.custom_total_fee) : "",
      });
    }
  }, [student?.id, student?.installments_schedule?.length]);

  const submitPayment = async () => {
    try {
      const payload = { student_id: id, amount: parseFloat(pay.amount), mode: pay.mode, notes: pay.notes };
      if (pay.installment_no) payload.installment_no = parseInt(pay.installment_no);
      if (pay.paid_on) payload.paid_on = new Date(pay.paid_on).toISOString();
      await api.post("/payments", payload);
      toast.success("Payment recorded");
      setOpenPay(false);
      setPay({ amount: "", mode: "upi", notes: "", installment_no: "", paid_on: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setupFees = async () => {
    try {
      const payload = {
        installments: parseInt(feeForm.installments) || 1,
        discount: parseFloat(feeForm.discount) || 0,
      };
      if (feeForm.custom_total) payload.custom_total = parseFloat(feeForm.custom_total);
      await api.post(`/students/${id}/fee-setup`, payload);
      toast.success("Fee structure saved");
      setOpenFee(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const approve = async () => {
    try {
      await api.post(`/students/${id}/approve`);
      toast.success("Student approved");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const removeStudent = async () => {
    try {
      await api.delete(`/students/${id}`);
      toast.success(`${student.name} deleted`);
      navigate("/students");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };


  const downloadReceipt = async (pid) => {
    const { data } = await api.get(`/payments/${pid}/receipt`);
    const w = window.open("", "_blank");
    w.document.write(receiptHtml(data));
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  const sendReminder = async (type) => {
    try {
      await api.post("/reminders/send", { student_ids: [id], template_type: type });
      toast.success("Reminder sent");
      load();
    } catch {
      toast.error("Failed to send reminder");
    }
  };

  const uploadDoc = async (docName, file) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post("/documents/upload", { student_id: id, doc_name: docName, file_data: reader.result, file_name: file.name });
        toast.success(`${docName} uploaded`);
        load();
      } catch {
        toast.error("Upload failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const viewDocument = async (docId, docName) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      setOpenDoc({ ...data, doc_name: docName });
    } catch {
      toast.error("Failed to load document");
    }
  };

  if (!student) return <div className="p-8 text-slate-500">Loading...</div>;

  const fs = student.fee_summary;
  const required = student.required_documents || student.program?.required_documents || [];
  const uploadedMap = Object.fromEntries((student.documents || []).map((d) => [d.doc_name, d]));
  const hasFees = (student.installments_schedule || []).length > 0;
  const programs = student.programs || (student.program ? [student.program] : []);

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="student-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
        <div className="flex items-start gap-5">
          {student.photo_url ? (
            <img src={student.photo_url} alt={student.name} className="w-24 h-28 object-cover rounded-md border-2 border-slate-200" />
          ) : (
            <div className="w-24 h-28 rounded-md bg-gradient-to-br from-slate-800 to-slate-900 text-white flex items-center justify-center text-3xl font-bold" style={{ fontFamily: "Chivo" }}>
              {student.name?.[0]}
            </div>
          )}
          <div>
            <div className="label-caps">{student.student_id}</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>{student.name}</h1>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant="outline" className={`status-${student.status}`}>{student.status}</Badge>
              <FeeBadgeLg status={fs.status} />
              {programs.map((p) => (
                <Badge key={p.id} variant="outline">{p.name}{p.is_lifetime ? " · Lifetime" : ""}</Badge>
              ))}
              {!student.approved && <Badge variant="outline" className="status-overdue">Pending Approval</Badge>}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Phone size={14} /> {student.phone}</span>
              {student.email && <span>✉ {student.email}</span>}
              <span className="flex items-center gap-1.5"><Calendar size={14} /> Enrolled {formatDate(student.enrollment_date)}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setOpenEdit(true)} className="h-11" data-testid="edit-student-button">
            <Pencil size={16} className="mr-2" /> Edit Details
          </Button>
          <Button variant="outline" onClick={() => setOpenCreds(true)} className="h-11" data-testid="set-credentials-button">
            <KeyRound size={16} className="mr-2" /> Login Credentials
          </Button>
          {!student.approved && (
            <Button onClick={approve} data-testid="approve-student-button" className="bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              <UserCheck size={16} className="mr-2" /> Approve
            </Button>
          )}
          {!hasFees ? (
            <Button onClick={() => setOpenFee(true)} data-testid="setup-fees-button" className="bg-slate-900 hover:bg-slate-800 h-11">
              <Settings size={16} className="mr-2" /> Setup Fees
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpenFee(true)} data-testid="reconfigure-fees-button" className="h-11">
                <Settings size={16} className="mr-2" /> Reconfigure Fees
              </Button>
              <Dialog open={openPay} onOpenChange={setOpenPay}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="record-payment-button">
                  <IndianRupee size={16} className="mr-2" /> Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="payment-dialog">
                <DialogHeader><DialogTitle>Record Payment · {student.name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label className="label-caps">Amount (₹)</Label><Input data-testid="pay-amount" type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-2 h-11" /></div>
                  <div>
                    <Label className="label-caps">Payment Date</Label>
                    <Input data-testid="pay-date" type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} className="mt-2 h-11" />
                    <p className="text-xs text-slate-500 mt-1">Defaults to today. Backdate if recording an offline/cash payment received earlier.</p>
                  </div>
                  <div>
                    <Label className="label-caps">Payment Mode</Label>
                    <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                      <SelectTrigger data-testid="pay-mode" className="mt-2 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps">Apply to installment</Label>
                    <Select value={pay.installment_no || ""} onValueChange={(v) => setPay({ ...pay, installment_no: v })}>
                      <SelectTrigger className="mt-2 h-11"><SelectValue placeholder="Auto-apply" /></SelectTrigger>
                      <SelectContent>
                        {(student.installments_schedule || []).filter((i) => !i.paid).map((i) => (
                          <SelectItem key={i.installment_no} value={String(i.installment_no)}>
                            Installment {i.installment_no} · {formatINR(i.amount)} · due {formatDate(i.due_date)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="label-caps">Notes</Label><Textarea data-testid="pay-notes" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} rows={2} className="mt-2" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenPay(false)}>Cancel</Button>
                  <Button data-testid="submit-payment" className="bg-slate-900 hover:bg-slate-800" onClick={submitPayment} disabled={!pay.amount}>Record Payment</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>
          )}
          <Button variant="outline" className="h-11" data-testid="send-reminder-button" onClick={() => sendReminder("due_soon")}>
            <MessageSquare size={16} className="mr-2" /> Send Reminder
          </Button>
          {user?.role === "super_admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="h-11 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="delete-student-button">
                  <Trash2 size={16} className="mr-2" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-student-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {student.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove the student, all their payments, receipts and uploaded documents. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={removeStudent} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-student">
                    Yes, delete permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Fee Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-5"><div className="label-caps mb-2">Total Fee</div><div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>{hasFees ? formatINR(fs.total) : "—"}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">Paid</div><div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }}>{formatINR(fs.paid)}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">Outstanding</div><div className="text-2xl font-bold text-red-700" style={{ fontFamily: "Chivo" }}>{hasFees ? formatINR(fs.outstanding) : "—"}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">Next Due</div><div className="text-sm font-semibold mt-1">{fs.next_due ? `${formatINR(fs.next_due.amount)} on ${formatDate(fs.next_due.due_date)}` : "—"}</div></Card>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fees & Payments</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({(student.documents || []).length}/{required.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Personal</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <KV label="Phone" v={student.phone} mono />
                <KV label="Email" v={student.email} />
                <KV label="Date of Birth" v={student.date_of_birth} />
                <KV label="Age" v={student.age} />
                <KV label="Blood Group" v={student.blood_group} />
                <KV label="Gender" v={student.gender} />
                <KV label="Address" v={student.address} wide />
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Parents</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <KV label="Father" v={student.father_name} />
                <KV label="Mother" v={student.mother_name} />
                <KV label="Parent Phone" v={student.parent_phone} mono />
                <KV label="Occupation" v={student.parent_occupation} />
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Background & Physical</h3>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <KV label="Religion" v={student.religion} />
                <KV label="Caste" v={student.caste} />
                <KV label="Category" v={student.category} />
                <KV label="Nationality" v={student.nationality} />
                <KV label="Height" v={student.height ? `${student.height} cm` : ""} />
                <KV label="Chest" v={student.chest ? `${student.chest} cm` : ""} />
                <KV label="Weight" v={student.weight ? `${student.weight} kg` : ""} />
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Education</h3>
              {(student.education || []).length === 0 ? <div className="text-sm text-slate-500">—</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr><th className="px-3 py-2 text-left label-caps">Class</th><th className="px-3 py-2 text-left label-caps">Year</th><th className="px-3 py-2 text-left label-caps">%</th></tr>
                  </thead>
                  <tbody>
                    {student.education.map((e, i) => (
                      <tr key={i} className="border-t"><td className="p-3">{e.class_name}</td><td className="p-3">{e.year}</td><td className="p-3 font-mono">{e.percentage}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Training</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <KV label="Programs" v={programs.map((p) => p.name).join(" + ")} />
                <KV label="Desired Post" v={student.desired_post} />
                <KV label="Training Duration" v={student.training_duration} />
                <KV label="Expected Completion" v={student.expected_completion_date ? formatDate(student.expected_completion_date) : (programs.every((p) => p.is_lifetime) ? "Lifetime" : "—")} />
              </div>
              {(student.facilities || []).length > 0 && (
                <div className="mt-4">
                  <div className="label-caps mb-2">Facilities Selected / आमच्या सुविधा</div>
                  <div className="flex flex-wrap gap-2">
                    {student.facilities.map((f) => (
                      <Badge key={f} variant="outline" className="text-xs">{facilityLabel(f)}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="fees">
          {!hasFees && (
            <Card className="p-6 mb-4 bg-amber-50 border-amber-200">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold flex items-center gap-2"><AlertCircle size={18} className="text-amber-700" /> Fee structure not set up</div>
                  <p className="text-sm text-slate-600 mt-1">Configure total fee, discount and installments to enable payment recording.</p>
                </div>
                <Button onClick={() => setOpenFee(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="setup-fees-cta">
                  <Settings size={16} className="mr-2" /> Setup Fees
                </Button>
              </div>
            </Card>
          )}

          {hasFees && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ fontFamily: "Chivo" }}>Installment Schedule</h3>
                <Button size="sm" variant="outline" onClick={() => setOpenFee(true)} data-testid="reconfigure-fees">
                  <Settings size={14} className="mr-1" /> Reconfigure
                </Button>
              </div>
              <div className="space-y-2">
                {(student.installments_schedule || []).map((i) => {
                  const overdue = !i.paid && new Date(i.due_date) < new Date();
                  return (
                    <div key={i.installment_no} className={`flex items-center justify-between p-3 rounded-md border ${i.paid ? "bg-emerald-50 border-emerald-200" : overdue ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
                      <div className="flex items-center gap-3">
                        {i.paid ? <Check size={18} className="text-emerald-600" /> : overdue ? <AlertCircle size={18} className="text-red-600" /> : <Calendar size={18} className="text-slate-500" />}
                        <div>
                          <div className="font-semibold">Installment {i.installment_no} · {formatINR(i.amount)}</div>
                          <div className="text-xs text-slate-500">Due {formatDate(i.due_date)} {i.paid && `· Paid ${formatDate(i.paid_on)}`}</div>
                        </div>
                      </div>
                      <Badge variant="outline" className={i.paid ? "status-paid" : overdue ? "status-overdue" : "status-due"}>
                        {i.paid ? "Paid" : overdue ? "Overdue" : "Due"}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Card className="p-6 mt-4">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Payment History</h3>
            {(student.payments || []).length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No payments recorded yet</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {student.payments.map((p) => (
                  <div key={p.id} className="py-3 flex items-center justify-between" data-testid={`payment-row-${p.id}`}>
                    <div>
                      <div className="font-semibold">{formatINR(p.amount)} · {p.mode.toUpperCase()}</div>
                      <div className="text-xs text-slate-500 font-mono">{p.receipt_no} · {formatDate(p.paid_on)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadReceipt(p.id)} data-testid={`receipt-${p.id}`}>
                      <Download size={14} className="mr-1" /> Receipt
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="p-6">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Required Documents</h3>
            <div className="space-y-2">
              {required.map((d) => {
                const uploaded = uploadedMap[d];
                return (
                  <div key={d} className={`flex items-center justify-between p-3 rounded-md border ${uploaded ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                    <div className="flex items-center gap-3">
                      {uploaded ? <Check size={18} className="text-emerald-600" /> : <X size={18} className="text-red-500" />}
                      <div>
                        <div className="font-medium">{d}</div>
                        {uploaded && <div className="text-xs text-slate-500">{uploaded.file_name} · uploaded {formatDate(uploaded.uploaded_at)} by {uploaded.uploaded_by}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {uploaded && (
                        <Button size="sm" variant="outline" onClick={() => viewDocument(uploaded.id, d)} data-testid={`view-doc-${d.replace(/\s+/g, "-")}`}>
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      )}
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(d, e.target.files[0])} data-testid={`upload-doc-${d.replace(/\s+/g, "-")}`} />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-md text-xs font-medium hover:bg-slate-100">
                          <Upload size={12} /> {uploaded ? "Replace" : "Upload"}
                        </span>
                      </label>
                    </div>
                  </div>
                );
              })}
              {required.length === 0 && <div className="text-sm text-slate-500">No documents required for this program.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Fee setup */}
      <Dialog open={openFee} onOpenChange={setOpenFee}>
        <DialogContent data-testid="fee-setup-dialog">
          <DialogHeader><DialogTitle>Configure Fee Structure</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600 p-3 bg-slate-50 rounded">
              Programs: <strong>{programs.map((p) => p.name).join(" + ")}</strong>
              <div className="mt-1">Base total: <strong>{formatINR(programs.reduce((s, p) => s + (p.total_fee || 0), 0))}</strong></div>
            </div>
            <div>
              <Label className="label-caps">Custom Total Fee (₹) — optional</Label>
              <Input data-testid="fee-custom" type="number" value={feeForm.custom_total} onChange={(e) => setFeeForm({ ...feeForm, custom_total: e.target.value })} className="mt-2 h-11" placeholder={`Leave blank to use sum of program fees`} />
            </div>
            <div>
              <Label className="label-caps">Discount (₹)</Label>
              <Input data-testid="fee-discount" type="number" value={feeForm.discount} onChange={(e) => setFeeForm({ ...feeForm, discount: e.target.value })} className="mt-2 h-11" />
            </div>
            <div>
              <Label className="label-caps">Number of Installments</Label>
              <Input data-testid="fee-installments" type="number" min="1" max="12" value={feeForm.installments} onChange={(e) => setFeeForm({ ...feeForm, installments: e.target.value })} className="mt-2 h-11" />
              <p className="text-xs text-slate-500 mt-1">1 = lump sum. Schedule is monthly from today.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenFee(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={setupFees} data-testid="save-fee-setup">Save Fee Structure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document viewer */}
      <Dialog open={!!openDoc} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{openDoc?.doc_name}</DialogTitle></DialogHeader>
          {openDoc && (
            <div className="max-h-[70vh] overflow-auto">
              {openDoc.file_data?.startsWith("data:application/pdf") ? (
                <iframe src={openDoc.file_data} title={openDoc.doc_name} className="w-full h-[70vh]" />
              ) : openDoc.file_data?.startsWith("data:image") ? (
                <img src={openDoc.file_data} alt={openDoc.doc_name} className="max-w-full mx-auto" />
              ) : (
                <a href={openDoc.file_data} download={openDoc.file_name} className="text-blue-600 underline">Download {openDoc.file_name}</a>
              )}
            </div>
          )}
          <DialogFooter>
            <a href={openDoc?.file_data} download={openDoc?.file_name}>
              <Button variant="outline"><Download size={14} className="mr-1" /> Download</Button>
            </a>
            <Button onClick={() => setOpenDoc(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditStudentDialog open={openEdit} onOpenChange={setOpenEdit} student={student} onSaved={load} />
      <StudentCredentialsDialog open={openCreds} onOpenChange={setOpenCreds} student={student} onSaved={load} />
    </div>
  );
}

function KV({ label, v, mono, wide }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <div className="label-caps">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

const FACILITY_LABELS = {
  dress_code: "Dress Code / ड्रेस कोड",
  ground: "Ground / ग्राऊंड",
  lecture: "Lecture / लेक्चर",
  study_hall: "Study Hall / अभ्यासिका",
  hostel: "Hostel / राहण्याची सोय",
  mess: "Mess / जेवणाची सोय",
};
function facilityLabel(k) { return FACILITY_LABELS[k] || k; }


function receiptHtml(r) {
  const a = r.academy;
  const logoUrl = a.logo_url?.startsWith("http") ? a.logo_url : `${window.location.origin}${a.logo_url || ""}`;
  const logo = a.logo_url ? `<img src="${logoUrl}" alt="logo" style="width:80px;height:80px;object-fit:contain;margin:0 auto 10px;display:block" />` : "";
  return `<!doctype html><html><head><title>Receipt ${r.payment.receipt_no}</title>
  <style>body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;max-width:640px;margin:auto;color:#0f172a}
  h1{font-size:22px;margin:0}.muted{color:#64748b}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #e2e8f0}
  .total{font-size:20px;font-weight:bold;padding:16px;background:#dcfce7;color:#166534;border-radius:8px;margin-top:20px}
  .addr{font-size:13px;color:#475569;margin-top:6px}</style></head>
  <body><div style="text-align:center;margin-bottom:24px;border-bottom:3px solid #0f172a;padding-bottom:16px">${logo}<h1>${a.name}</h1>
  <div class="muted" style="font-size:12px">${a.name_en || ""}</div>
  <div class="addr">${a.address}</div>
  ${a.phone ? `<div class="addr">मोबाईल नंबर - ${a.phone}</div>` : ""}</div>
  <div><div class="row"><span class="muted">Receipt No</span><strong>${r.payment.receipt_no}</strong></div>
  <div class="row"><span class="muted">Date</span><strong>${new Date(r.payment.paid_on).toLocaleString("en-IN")}</strong></div>
  <div class="row"><span class="muted">Student</span><strong>${r.student.name} (${r.student.student_id})</strong></div>
  <div class="row"><span class="muted">Phone</span><strong>${r.student.phone}</strong></div>
  <div class="row"><span class="muted">Program</span><strong>${r.program?.name || "-"}</strong></div>
  <div class="row"><span class="muted">Payment Mode</span><strong>${r.payment.mode.toUpperCase()}</strong></div>
  ${r.payment.installment_no ? `<div class="row"><span class="muted">Installment</span><strong>#${r.payment.installment_no}</strong></div>` : ""}
  <div class="total">Amount Paid: ₹${r.payment.amount.toLocaleString("en-IN")}</div>
  ${r.payment.notes ? `<div style="margin-top:20px"><span class="muted">Notes:</span> ${r.payment.notes}</div>` : ""}
  <div style="margin-top:40px;text-align:center" class="muted">Thank you. Computer-generated receipt.</div></div></body></html>`;
}
